'use client';
import React, { createContext, useEffect, useState } from 'react';
import { AuthData } from 'auth-types';
import { usePathname, useRouter } from 'next/navigation';
import { role } from 'auth-types';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { NavItemMap, NavItem } from '@/services/nav/nav-item-map';
import {
  setAdminRole,
  setisLoggedIn,
  setRole,
  setUserEmail,
  setUserFirstName,
  setUserLastName,
} from '@/lib/slices/login-slice';
import AuthService from '@/services/auth/auth-service';
import Request from '@/services/util/request-service';

type AuthContextType = {
  authData: AuthData;
  setAuthData: (data: any) => void;
};

const defaultValue: AuthContextType = {
  authData: { token: '' },
  setAuthData: () => {},
};

const AuthContext = createContext<AuthContextType>(defaultValue);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);

  const [authData, setAuthData] = useState<AuthData>(defaultValue.authData);

  useEffect(() => {
    dispatch(setisLoggedIn(AuthService.isLoggedIn()));
  }, []);

  useEffect(() => {
    if (loggedIn && userRole == '') {
      getUserInfo();
    }
  }, [loggedIn]);

  const getUserInfo = async () => {
    const response = await (await Request.get('user/getUserBasicInfo'))?.json();
    if (!response || response.error || !response.role) {
      return;
    }
    dispatch(setRole(response.role));
    dispatch(setAdminRole(response.admin_role ?? null));
    dispatch(setUserFirstName(response.first_name));
    dispatch(setUserLastName(response.last_name));
    dispatch(setUserEmail(response.email));
    return;
  };

  useEffect(() => {
    if (userRole !== '') {
      // Function to check if the current path matches any top-level or nested routes

      const matchesRoute = (route: string): boolean => {
        // Accept exact, with trailing slash, or nested paths
        return pathname === route || pathname === `${route}/` || pathname.startsWith(`${route}/`);
      };

      const isValidRoute = (items: NavItem[]): boolean => {
        return items.some((item) => {
          if (matchesRoute(item.route)) return true;
          return item.subItems ? isValidRoute(item.subItems) : false;
        });
      };

      // Validate the current path against the navigation map for the user's role
      const validPath = isValidRoute(NavItemMap[userRole]);

      // If no valid path is found, redirect to the first route available for this role
      if (!validPath) {
        const firstRoute = NavItemMap[userRole][0];

        // If the first route has subItems, navigate to the first subItem's route, otherwise use the main route
        const targetRoute = firstRoute.subItems ? firstRoute.subItems[0].route : firstRoute.route;
        if (!matchesRoute(targetRoute)) {
          router.push(targetRoute);
        }
      }
    }
  }, [userRole, pathname, router]);

  return <AuthContext.Provider value={{ authData, setAuthData }}>{children}</AuthContext.Provider>;
}

export { AuthProvider, AuthContext };
